import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelIcon from "@mui/icons-material/Cancel";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PropTypes from "prop-types";
import moment from "moment";

import ProfileViewer from "../../../components/shared/ProfileViewer";
import CompanyViewer from "../../../components/shared/CompanyViewer";
import { Roles } from "../../../utilities/constants";
import { getCurrentUserSelector, getUserSelector } from "../../../redux/users/selector";
import { getUserById, deleteUser, patchUser, getUsers } from "../../../redux/users/actions";
import PrimaryButton from "../../../components/shared/PrimaryButton";
import SecondaryButton from "../../../components/shared/SecondaryButton";
// import UserForm from "../../../components/shared/ProfileForm";
import UserFormV2 from "../../../components/shared/ProfileFormV2";
import { getDocumentsForUser } from "../../../redux/documents/selectors";
import { getUserDocuments } from "../../../redux/documents/actions";
import DocumentsTable from "../../../components/shared/DocumentsTable";
import CustomTabs from "../../../components/shared/Tabs";
import PromoterViewer from "../../../components/shared/PromoterViewer";
import PdfProfileViewButton from "../../../components/shared/ProfileViewer/PdfProfileViewButton";
import { capitalizeName, capitalizeFirstLetter } from "../../../utilities";

// Delete Confirmation Popup Component
const DeleteConfirmationPopup = ({ open, onClose, onConfirm, onUndo, userName, countdown }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ color: "error.main", fontWeight: "bold" }}>Delete User Confirmation</DialogTitle>
    <DialogContent>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Are you sure you want to delete <strong>{capitalizeName(userName)}</strong>?
      </Typography>
      <Typography variant="body2" color="text.secondary">
        User will be deleted in {countdown} seconds. Click &quot;Undo&quot; to cancel.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
      <SecondaryButton onClick={onUndo} color="primary">
        Undo
      </SecondaryButton>
      <PrimaryButton onClick={onConfirm} color="error" variant="contained">
        Delete Now
      </PrimaryButton>
    </DialogActions>
  </Dialog>
);

// Add PropTypes validation
DeleteConfirmationPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onUndo: PropTypes.func.isRequired,
  userName: PropTypes.string.isRequired,
  countdown: PropTypes.number.isRequired,
};

const SingleUser = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const currentUser = useSelector(getCurrentUserSelector);
  const selectedUser = useSelector(getUserSelector(params.id));
  const documents = useSelector(getDocumentsForUser(selectedUser?.id));
  const previousPath = useSelector((state) => state.utilities.previousPath);
  const allUsers = useSelector((state) => state.users.data || []);
  const promoters = allUsers.filter((user) => user.role === Roles.PROMOTER.value);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    userName: "",
    userId: null,
    countdown: 20,
    timer: null,
    interval: null,
  });
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [accountDetails, setAccountDetails] = useState(null);
  const [hasAccountChanges, setHasAccountChanges] = useState(false);
  const [savingAccountDetails, setSavingAccountDetails] = useState(false);

  const currentTab = searchParams.get("tab");
  const isInfoTab = currentTab === "view-profile/info";

  useEffect(() => {
    if (selectedUser && selectedUser?.role === Roles.USER.value) {
      dispatch(getUserDocuments({ userId: selectedUser.id }));
    }
  }, [selectedUser?.id]);

  useEffect(() => {
    dispatch(getUsers());
  }, []);

  useEffect(() => {
    dispatch(getUserById({ id: params.id }))
      .unwrap()
      .then((data) => {
        if (currentUser.role === Roles.COMPANY.value && data.role === Roles.COMPANY.value) {
          Cookies.remove("isFromDashboard");
          navigate(-1);
        }
      })
      .catch(() => {
        Cookies.remove("isFromDashboard");
        navigate(-1);
      });
  }, [params.id]);

  useEffect(() => {
    if (selectedUser) {
      setAccountDetails({
        email: selectedUser.email || "",
        role: selectedUser.role || "",
        referralEmail: selectedUser.referral || "",
        isEmailVerified: Boolean(selectedUser.isEmailVerified),
        isAccountVerified: Boolean(selectedUser.isAccountVerified),
      });
      setHasAccountChanges(false);
    }
  }, [selectedUser?.email, selectedUser?.role, selectedUser?.referral, selectedUser?.isEmailVerified, selectedUser?.isAccountVerified]);

  /* eslint-disable arrow-body-style */
  useEffect(() => {
    return () => {
      if (deleteConfirmation.timer) clearTimeout(deleteConfirmation.timer);
      if (deleteConfirmation.interval) clearInterval(deleteConfirmation.interval);
    };
  }, []);
  /* eslint-enable arrow-body-style */

  const setUserBlocked = (shouldBlock) => {
    if (!selectedUser) return;
    dispatch(patchUser({ id: selectedUser.id, isEnabled: !shouldBlock }));
  };

  const handleDeleteUser = (rowId) => {
    const userName = selectedUser?.firstName || "this user";

    // Clear any existing timers
    if (deleteConfirmation.timer) clearTimeout(deleteConfirmation.timer);
    if (deleteConfirmation.interval) clearInterval(deleteConfirmation.interval);

    setDeleteConfirmation({
      open: true,
      userName,
      userId: rowId,
      countdown: 20,
      timer: null,
      interval: null,
    });

    // Start countdown
    const interval = setInterval(() => {
      setDeleteConfirmation((prev) => {
        if (prev.countdown <= 1) {
          clearInterval(interval);
          return prev;
        }
        return { ...prev, countdown: prev.countdown - 1 };
      });
    }, 1000);

    // Auto-delete after 20 seconds
    const timer = setTimeout(() => {
      clearInterval(interval);
      dispatch(deleteUser(rowId)).then(() => {
        navigate(previousPath || "/users");
      });
      setDeleteConfirmation((prev) => ({ ...prev, open: false }));
    }, 20000);

    // Store timer references
    setDeleteConfirmation((prev) => ({
      ...prev,
      timer,
      interval,
    }));
  };

  const updateAccountDetails = (field, value) => {
    setAccountDetails((prev) => {
      const next = { ...prev, [field]: value };
      return next;
    });
    setHasAccountChanges(true);
  };

  const handleAccountCheckboxChange = (field) => (event) => {
    updateAccountDetails(field, event.target.checked);
  };

  const handleAccountFieldChange = (field) => (event) => {
    updateAccountDetails(field, event.target.value);
  };

  const handleSaveAccountDetails = () => {
    if (!selectedUser || !accountDetails) return;
    setSavingAccountDetails(true);
    dispatch(patchUser({ id: selectedUser.id, ...accountDetails }))
      .unwrap()
      .then(() => {
        setHasAccountChanges(false);
      })
      .finally(() => {
        setSavingAccountDetails(false);
      });
  };

  const getViewComponent = () => {
    if (selectedUser.role === Roles.USER.value) {
      if (currentUser.role === Roles.ADMIN.value && isInfoTab) {
        if (!accountDetails) {
          return null;
        }

        return (
          <Box sx={{ mt: 4, width: "100%", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              Account Details
            </Typography>
            <Box sx={{ mt: 4, width: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start", width: "100%" }}>
                <TextField sx={{ width: "30%" }} label="Email" value={accountDetails.email} onChange={handleAccountFieldChange("email")} size="small" type="email" InputProps={{ readOnly: true }} />
                <TextField
                  sx={{ width: "30%" }}
                  label="Registration Date"
                  value={selectedUser?.createdAt ? capitalizeFirstLetter(moment(selectedUser.createdAt).format("DD/MM/YYYY hh:mm")) : ""}
                  size="small"
                  InputProps={{ readOnly: true }}
                />
                <FormControl size="small" sx={{ width: "15%" }}>
                  <InputLabel id="account-role-label">Role</InputLabel>
                  <Select labelId="account-role-label" label="Role" value={accountDetails.role} onChange={handleAccountFieldChange("role")}>
                    <MenuItem value={Roles.USER.value}>User</MenuItem>
                    <MenuItem value={Roles.ADMIN.value}>Admin</MenuItem>
                    <MenuItem value={Roles.COMPANY.value}>Company</MenuItem>
                    <MenuItem value={Roles.PROMOTER.value}>Promoter</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ width: "15%" }}>
                  <InputLabel id="promoter-label">Promoter</InputLabel>
                  <Select labelId="promoter-label" label="Promoter" value={accountDetails.referralEmail || ""} onChange={handleAccountFieldChange("referralEmail")}>
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {promoters.map((promoter) => (
                      <MenuItem key={promoter.email} value={promoter.email}>
                        {promoter.promoter?.firstName || promoter.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, alignItems: "flex-start" }}>
                  <FormGroup sx={{ alignItems: "flex-start" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={accountDetails.isEmailVerified}
                          onChange={handleAccountCheckboxChange("isEmailVerified")}
                          sx={{
                            color: "#1976d2",
                            "&.Mui-checked": { color: "#1976d2" },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          Email Verified
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={accountDetails.isAccountVerified}
                          onChange={handleAccountCheckboxChange("isAccountVerified")}
                          sx={{
                            color: "#1976d2",
                            "&.Mui-checked": { color: "#1976d2" },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          Account Verified
                        </Typography>
                      }
                    />
                  </FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!selectedUser.isEnabled}
                        onChange={(e) => setUserBlocked(e.target.checked)}
                        sx={{
                          color: "#d32f2f",
                          "&.Mui-checked": {
                            color: "#d32f2f",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: !selectedUser.isEnabled ? "#d32f2f" : "#666" }}>
                        User Block
                      </Typography>
                    }
                  />
                  <PrimaryButton onClick={() => handleDeleteUser(selectedUser.id)}>Delete</PrimaryButton>
                  <PrimaryButton onClick={handleSaveAccountDetails} disabled={!hasAccountChanges || savingAccountDetails}>
                    {savingAccountDetails ? "Saving..." : "Save"}
                  </PrimaryButton>
                </Box>
              </Box>
            </Box>
          </Box>
        );
      }

      return <ProfileViewer user={currentUser} profile={{ email: selectedUser.email, ...selectedUser.profile }} />;
    }
    if (selectedUser.role === Roles.COMPANY.value) {
      return <CompanyViewer data={selectedUser.company} />;
    }
    if (selectedUser.role === Roles.PROMOTER.value) {
      return <PromoterViewer data={selectedUser.promoter} />;
    }
    return null;
  };

  const handleConfirmDelete = () => {
    // Clear timers
    if (deleteConfirmation.timer) clearTimeout(deleteConfirmation.timer);
    if (deleteConfirmation.interval) clearInterval(deleteConfirmation.interval);

    // Delete immediately
    dispatch(deleteUser(deleteConfirmation.userId)).then(() => {
      navigate(previousPath || "/users");
    });
    setDeleteConfirmation((prev) => ({ ...prev, open: false }));
  };

  const handleUndoDelete = () => {
    // Clear timers to cancel the deletion
    if (deleteConfirmation.timer) clearTimeout(deleteConfirmation.timer);
    if (deleteConfirmation.interval) clearInterval(deleteConfirmation.interval);

    setDeleteConfirmation((prev) => ({ ...prev, open: false }));
  };

  const handleDownloadMenuOpen = (event) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  const handleDownloadOption = (privacy) => {
    // Close the menu first
    handleDownloadMenuClose();

    // Find the hidden PDF button and trigger its download
    const pdfButton = document.querySelector(`[data-privacy="${privacy}"]`);
    if (pdfButton) {
      const downloadLink = pdfButton.querySelector("a[download]");
      if (downloadLink) {
        downloadLink.click();
      }
    }
  };

  return (
    currentUser &&
    selectedUser && (
      <Box
        sx={{
          width: {
            xs: "100%",
            sm: "100%",
            md: "80%",
          },
          margin: "auto",
        }}>
        <CustomTabs
          tabs={[
            {
              label: "View Profile",
              value: "view-profile",
              component: getViewComponent(),
              onClick: () => navigate("?tab=view-profile"),
            },
            // ...(currentUser.role === Roles.ADMIN.value || currentUser.role === Roles.PROMOTER.value
            //   ? [
            //     {
            //       label: "Edit Profile",
            //       value: "edit-profile",
            //       component: <UserForm userId={selectedUser.id} canSkipSteps />,
            //       onClick: () => navigate("?tab=edit-profile"),
            //     }
            //   ]
            //   : []),
            ...(currentUser.role === Roles.ADMIN.value || currentUser.role === Roles.PROMOTER.value
              ? [
                  {
                    label: "Edit Profile",
                    value: "edit-profile-v2",
                    component: <UserFormV2 userId={selectedUser.id} canSkipSteps />,
                    onClick: () => navigate("?tab=edit-profile-v2"),
                  },
                ]
              : []),
            ...((currentUser.role === Roles.ADMIN.value || currentUser.role === Roles.PROMOTER.value) && selectedUser?.role === Roles.USER.value
              ? [
                  {
                    label: "Documents",
                    value: "documents",
                    component: <DocumentsTable userId={selectedUser.id} loading={documents?.loading} documents={documents?.data || []} />,
                    onClick: () => navigate("?tab=documents"),
                  },
                ]
              : []),
          ]}
          defaultTab="view-profile"
          header={
            <Grid container spacing={2} alignItems="center">
              {currentUser.role === Roles.USER.value && (
                <Grid item xs={12} md={4} lg={4}>
                  <Box>
                    <Typography variant="h4">
                      Verified: {selectedUser?.isAccountVerified ? <VerifiedIcon sx={{ color: "#C02026", marginLeft: "5px" }} /> : <CancelIcon sx={{ color: "#C02026", marginLeft: "5px" }} />}
                    </Typography>
                  </Box>
                </Grid>
              )}

              <Grid item xs={12} md={8} lg={8}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    justifyContent: "flex-end",
                    flexWrap: "nowrap",
                    alignItems: "center",
                  }}>
                  {(currentUser.role === Roles.ADMIN.value || currentUser.role === Roles.PROMOTER.value) && selectedUser.role === Roles.USER.value && (
                    <>
                      {/* Hidden PDF buttons for programmatic triggering */}
                      <Box sx={{ display: "none" }} data-privacy="normal">
                        <PdfProfileViewButton privacy="normal" selectedUser={selectedUser} buttonText="Download PDF" />
                      </Box>
                      <Box sx={{ display: "none" }} data-privacy="min">
                        <PdfProfileViewButton privacy="min" selectedUser={selectedUser} buttonText="Download MIN PDF" />
                      </Box>
                      <Box sx={{ display: "none" }} data-privacy="full">
                        <PdfProfileViewButton privacy="full" selectedUser={selectedUser} buttonText="Download FULL PDF" />
                      </Box>

                      {/* Info button */}
                      <Box sx={{ flexShrink: 0 }}>
                        <Button
                          variant="text"
                          onClick={() => navigate(`/users/${params.id}?tab=view-profile/info`)}
                          sx={{
                            color: "#d32f2f",
                            "&:hover": {
                              color: "#ef5350",
                            },
                          }}>
                          info
                        </Button>
                      </Box>

                      {/* Download dropdown button */}
                      <Box sx={{ flexShrink: 0 }}>
                        <Button
                          variant="contained"
                          onClick={handleDownloadMenuOpen}
                          endIcon={<KeyboardArrowDownIcon />}
                          sx={{
                            backgroundColor: "#d32f2f",
                            color: "white",
                            "&:hover": {
                              backgroundColor: "#b71c1c",
                            },
                            borderRadius: "8px",
                            px: 3,
                            py: 1,
                          }}>
                          Download
                        </Button>
                        <Menu
                          anchorEl={downloadMenuAnchor}
                          open={Boolean(downloadMenuAnchor)}
                          onClose={handleDownloadMenuClose}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}>
                          <MenuItem onClick={() => handleDownloadOption("normal")}>Download PDF</MenuItem>
                          <MenuItem onClick={() => handleDownloadOption("min")}>Download MIN PDF</MenuItem>
                          <MenuItem onClick={() => handleDownloadOption("full")}>Download FULL PDF</MenuItem>
                        </Menu>
                      </Box>
                    </>
                  )}
                  <Box sx={{ flexShrink: 0 }}>
                    <PrimaryButton
                      onClick={() => {
                        const isFromDashboard = Cookies.get("isFromDashboard");

                        Cookies.remove("isFromDashboard");

                        navigate(isFromDashboard ? "/dashboard" : "/users");
                      }}>
                      Back
                    </PrimaryButton>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          }
        />
        <DeleteConfirmationPopup
          open={deleteConfirmation.open}
          onClose={() => setDeleteConfirmation((prev) => ({ ...prev, open: false }))}
          onConfirm={handleConfirmDelete}
          onUndo={handleUndoDelete}
          userName={deleteConfirmation.userName}
          countdown={deleteConfirmation.countdown}
        />
      </Box>
    )
  );
};

export default SingleUser;
